import { maskSsnLikeInText } from '@ups/xplat/base/ryn-replay';

/**
 * Bug 35675: mask SSN-shaped text in every rrweb event, whatever the app's masking policy says.
 *
 * rrweb's own text masking (`maskTextSelector` / `maskTextFn`) only runs on nodes a selector or class already marks, and
 * `maskAllText` is opt-in (only the packet apps turn it on), so on every other app an SSN rendered as text, typed into an
 * unmasked input or carried in a tooltip reached replay storage in clear. rrweb 2.1 also has no attribute-masking hook.
 * Masking the serialised event instead covers all of it in one place: FullSnapshot text and attributes, every
 * IncrementalSnapshot mutation (text diffs, attribute diffs, added subtrees) and Input events.
 *
 * Reference-preserving like the URL scrub: an event with nothing SSN-shaped is returned as is, never cloned.
 * Known limit: an SSN split across two text nodes is not joined; render sites that split one are PR B's job.
 */

// rrweb numbers, kept local and explicit (same reason as the URL scrub) so a rrweb major cannot renumber them silently.
const RRWEB_FULL_SNAPSHOT_EVENT_TYPE = 2;
const RRWEB_INCREMENTAL_SNAPSHOT_EVENT_TYPE = 3;
const RRWEB_MUTATION_SOURCE = 0;
const RRWEB_INPUT_SOURCE = 5;

/** Attributes whose value is visible or announced text (tooltips, labels, placeholders, input values). */
const isTextAttribute = (name: string): boolean =>
  name === 'value' ||
  name === 'title' ||
  name === 'placeholder' ||
  name === 'alt' ||
  name === 'label' ||
  name.startsWith('aria-');

const maskAttributes = (
  attributes: Record<string, unknown>
): Record<string, unknown> => {
  let next: Record<string, unknown> | undefined;
  for (const [name, value] of Object.entries(attributes)) {
    if (typeof value === 'string' && isTextAttribute(name)) {
      const masked = maskSsnLikeInText(value);
      if (masked !== value) {
        next ??= { ...attributes };
        next[name] = masked;
      }
    }
  }
  return next ?? attributes;
};

type SerializedNode = {
  textContent?: unknown;
  isStyle?: boolean;
  attributes?: Record<string, unknown>;
  childNodes?: unknown[];
};

const maskNode = (node: unknown): unknown => {
  if (!node || typeof node !== 'object') {
    return node;
  }
  const n = node as SerializedNode;
  let next: SerializedNode | undefined;

  // Inlined stylesheet text (isStyle) is CSS, never page content.
  if (typeof n.textContent === 'string' && !n.isStyle) {
    const masked = maskSsnLikeInText(n.textContent);
    if (masked !== n.textContent) {
      next = { ...n, textContent: masked };
    }
  }
  if (n.attributes && typeof n.attributes === 'object') {
    const masked = maskAttributes(n.attributes);
    if (masked !== n.attributes) {
      next = { ...(next ?? n), attributes: masked };
    }
  }
  if (Array.isArray(n.childNodes)) {
    const kids = n.childNodes.map(maskNode);
    if (kids.some((kid, i) => kid !== n.childNodes![i])) {
      next = { ...(next ?? n), childNodes: kids };
    }
  }
  return next ?? node;
};

/** Map a record array, cloning only when an element changed. */
const mapChanged = <T>(items: unknown, fn: (item: T) => T): T[] | undefined => {
  if (!Array.isArray(items)) {
    return undefined;
  }
  const next = items.map((item) =>
    item && typeof item === 'object' ? fn(item as T) : item
  );
  return next.some((item, i) => item !== items[i]) ? next : undefined;
};

const maskMutation = (
  data: Record<string, unknown>
): Record<string, unknown> => {
  const texts = mapChanged<{ value?: unknown }>(data['texts'], (t) => {
    if (typeof t.value !== 'string') {
      return t;
    }
    const value = maskSsnLikeInText(t.value);
    return value === t.value ? t : { ...t, value };
  });
  const attributes = mapChanged<{ attributes?: Record<string, unknown> }>(
    data['attributes'],
    (r) => {
      if (!r.attributes || typeof r.attributes !== 'object') {
        return r;
      }
      const masked = maskAttributes(r.attributes);
      return masked === r.attributes ? r : { ...r, attributes: masked };
    }
  );
  const adds = mapChanged<{ node?: unknown }>(data['adds'], (a) => {
    const node = maskNode(a.node);
    return node === a.node ? a : { ...a, node };
  });
  if (!texts && !attributes && !adds) {
    return data;
  }
  return {
    ...data,
    ...(texts ? { texts } : {}),
    ...(attributes ? { attributes } : {}),
    ...(adds ? { adds } : {}),
  };
};

/** Mask SSN-shaped text everywhere an rrweb event serialises page text (bug 35675). Policy-independent by design. */
export const maskRynReplayEventSsn = (event: unknown): unknown => {
  if (!event || typeof event !== 'object') {
    return event;
  }
  const typed = event as { type?: number; data?: Record<string, unknown> };
  const data = typed.data;
  if (!data || typeof data !== 'object') {
    return event;
  }

  if (typed.type === RRWEB_FULL_SNAPSHOT_EVENT_TYPE) {
    const node = maskNode(data['node']);
    return node === data['node']
      ? event
      : { ...typed, data: { ...data, node } };
  }

  if (typed.type === RRWEB_INCREMENTAL_SNAPSHOT_EVENT_TYPE) {
    if (data['source'] === RRWEB_MUTATION_SOURCE) {
      const masked = maskMutation(data);
      return masked === data ? event : { ...typed, data: masked };
    }
    if (
      data['source'] === RRWEB_INPUT_SOURCE &&
      typeof data['text'] === 'string'
    ) {
      const text = maskSsnLikeInText(data['text']);
      return text === data['text']
        ? event
        : { ...typed, data: { ...data, text } };
    }
  }
  return event;
};
